/**
 * CalendarEventCard - v2 content for calendar event creation actions.
 *
 * Read mode: renders MeetingCardContent with datetime boxes, attendees, and location.
 * Edit mode: datetime boxes become clickable to open a DateTimeRangePicker,
 * title and location are editable text fields.
 * Shell is provided by the parent.
 */

import { MeetingCardContent } from '@/lib/crm/object-cards/MeetingCardContent';

import type {
  CalendarEventCreatePayload,
  ProposedAction,
} from '../../lib/proposals/core/types';
import { isCalendarEventCreatePayload } from '../../lib/proposals/core/types';
import { useResolvedCalendarSlot } from '../../lib/proposals/hooks/useResolvedCalendarSlot';
import type { ActionRendererProps } from '../../lib/proposals/registry/types';
import { useActionEdit } from './ActionAccordion';
import { CalendarEventEditView } from './CalendarEventCard.EditView';
import { ActionError } from './shared';
import { ACTION_CARD_BODY_CLASS } from './shared/actionCardLayout';

// ─── Read-only view ─────────────────────────────────────────────────────────

function CalendarEventReadOnly({
  action,
  orgId,
}: {
  action: ProposedAction & { payload: CalendarEventCreatePayload };
  orgId?: number;
}) {
  const params = action.payload.action_params;
  const { attendees, location } = params;
  // Never coerce a missing stored time into a date (see useResolvedCalendarSlot).
  const resolved = useResolvedCalendarSlot(params.start_time, params.end_time);

  return (
    <div className={ACTION_CARD_BODY_CLASS}>
      <MeetingCardContent
        title={params.title || 'Calendar Event'}
        startTime={resolved.startTime}
        endTime={resolved.endTime}
        attendees={attendees?.map((email: string) => ({
          name: email.split('@')[0],
          email,
        }))}
        location={location}
        orgId={orgId}
      />
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export function CalendarEventCard({
  action,
  mode,
  orgId,
  proposalId,
  onUpdate,
}: ActionRendererProps) {
  const { isEditing } = useActionEdit();

  if (!isCalendarEventCreatePayload(action.payload)) {
    return <ActionError message='Invalid calendar event payload' mode={mode} />;
  }

  const typedAction = action as ProposedAction & {
    payload: CalendarEventCreatePayload;
  };

  if (isEditing) {
    return (
      <CalendarEventEditView
        action={typedAction}
        proposalId={proposalId}
        orgId={orgId}
        onUpdate={onUpdate}
      />
    );
  }

  return <CalendarEventReadOnly action={typedAction} orgId={orgId} />;
}
